/**
 * useCalendarData Hook
 * React Query hook for managing calendar data and mutations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { calendarService } from '../services/calendarService'
import { clientLogger } from '../utils/clientLogger'
import { recipeApiService } from '../services/recipeApiService'
import {
  CalendarData,
  DateRange,
  AssignRecipeParams,
  RemoveAssignmentParams,
  MealAssignment,
  AssignmentMap
} from '../types/calendar'
import { getAssignmentKey } from '../utils/calendarUtils'

interface UseCalendarDataParams {
  userId: string | null
  sessionId: string | null
  dateRange: DateRange
  enabled?: boolean // Allow disabling the query
}

interface UseCalendarDataReturn {
  // Data
  assignments: AssignmentMap
  recipes: any[]
  unassignedRecipes: any[]
  enabledMealTypes: string[]

  // Loading & Error States
  isLoading: boolean
  isError: boolean
  error: Error | null

  // Mutations
  assignRecipe: (params: AssignRecipeParams) => void
  removeAssignment: (params: RemoveAssignmentParams) => void
  isAssigning: boolean
  isRemoving: boolean

  // Utils
  refetch: () => void
}

/**
 * Hook for managing calendar data with React Query
 * Handles fetching, caching, and mutations for calendar operations
 */
export function useCalendarData({
  userId,
  sessionId,
  dateRange,
  enabled = true
}: UseCalendarDataParams): UseCalendarDataReturn {
  const queryClient = useQueryClient()

  // Generate stable query key
  const queryKey = [
    'calendar',
    userId,
    dateRange.start.toISOString(),
    dateRange.end.toISOString()
  ]

  // Fetch calendar data
  const {
    data,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery<CalendarData, Error>({
    queryKey,
    queryFn: async () => {
      if (!userId) {
        throw new Error('User ID is required')
      }
      return calendarService.getCalendarData(userId, sessionId, dateRange)
    },
    enabled: enabled && !!userId, // Only fetch if enabled and userId exists
    staleTime: 2 * 60 * 1000, // Consider data fresh for 2 minutes
    refetchOnWindowFocus: true, // Refresh when user returns to tab
    retry: 2 // Retry failed requests twice
  })

  // Assign recipe mutation with optimistic updates
  const assignMutation = useMutation({
    mutationFn: async (params: AssignRecipeParams) => {
      const { recipeId, date } = params

      // Find the recipe in current data
      const recipe = data?.recipes.find(r => r.id === recipeId)
      if (!recipe) {
        throw new Error('Recipe not found')
      }

      // Calculate new dates array
      const currentDates = recipe.assigned_dates || []
      const newDates = [...new Set([...currentDates, date])] // Use Set to avoid duplicates

      // Update via API
      return recipeApiService.updateRecipeAssignedDates(recipeId, newDates)
    },

    onMutate: async (params) => {
      const { recipeId, date, mealType } = params

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey })

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<CalendarData>(queryKey)

      // Optimistically update the cache
      queryClient.setQueryData<CalendarData>(queryKey, (old) => {
        if (!old) return old

        const recipe = old.recipes.find(r => r.id === recipeId)
        if (!recipe) return old

        // Create new assignment
        const newAssignment: MealAssignment = {
          id: `${recipeId}_${date}`,
          recipeId,
          recipeName: recipe.recipe_data.name,
          mealType,
          date,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }

        // Update assignments map
        const newAssignments = new Map(old.assignments)
        const key = getAssignmentKey(date, mealType)
        newAssignments.set(key, newAssignment)

        // Update recipe's assigned_dates
        const updatedRecipes = old.recipes.map(r => {
          if (r.id === recipeId) {
            const currentDates = r.assigned_dates || []
            return {
              ...r,
              assigned_dates: [...new Set([...currentDates, date])]
            }
          }
          return r
        })

        // Remove from unassigned if it was there
        const updatedUnassigned = old.unassignedRecipes.filter(r => r.id !== recipeId)

        return {
          ...old,
          assignments: newAssignments,
          recipes: updatedRecipes,
          unassignedRecipes: updatedUnassigned
        }
      })

      return { previousData }
    },

    onError: (err, _params, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData)
      }
      clientLogger.error({ err: err instanceof Error ? err : new Error(String(err)) }, 'Failed to assign recipe')
    }

    // Note: No onSettled - we rely on optimistic updates for instant UI feedback
    // The cache will naturally sync on next mount or manual refetch
  })

  // Remove assignment mutation with optimistic updates
  const removeMutation = useMutation({
    mutationFn: async (params: RemoveAssignmentParams) => {
      const { recipeId, date } = params

      // Find the recipe
      const recipe = data?.recipes.find(r => r.id === recipeId)
      if (!recipe) {
        throw new Error('Recipe not found')
      }

      // Calculate new dates array (remove the specified date)
      const currentDates = recipe.assigned_dates || []
      const newDates = currentDates.filter((d: string) => d !== date)

      // Update via API
      return recipeApiService.updateRecipeAssignedDates(recipeId, newDates)
    },

    onMutate: async (params) => {
      const { recipeId, date, mealType } = params

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey })

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<CalendarData>(queryKey)

      // Optimistically update the cache
      queryClient.setQueryData<CalendarData>(queryKey, (old) => {
        if (!old) return old

        // Remove from assignments map
        const newAssignments = new Map(old.assignments)
        const key = getAssignmentKey(date, mealType)
        newAssignments.delete(key)

        // Update recipe's assigned_dates
        const updatedRecipes = old.recipes.map(r => {
          if (r.id === recipeId) {
            const newDates = (r.assigned_dates || []).filter((d: string) => d !== date)
            return {
              ...r,
              assigned_dates: newDates
            }
          }
          return r
        })

        // Check if recipe should move to unassigned
        const recipe = old.recipes.find(r => r.id === recipeId)
        const hasOtherDates = recipe?.assigned_dates?.some((d: string) => d !== date)
        const updatedUnassigned = hasOtherDates || !recipe
          ? old.unassignedRecipes
          : [...old.unassignedRecipes, recipe]

        return {
          ...old,
          assignments: newAssignments,
          recipes: updatedRecipes,
          unassignedRecipes: updatedUnassigned
        }
      })

      return { previousData }
    },

    onError: (err, _params, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData)
      }
      clientLogger.error({ err: err instanceof Error ? err : new Error(String(err)) }, 'Failed to remove assignment')
    }

    // Note: No onSettled - we rely on optimistic updates for instant UI feedback
    // The cache will naturally sync on next mount or manual refetch
  })

  return {
    // Data
    assignments: data?.assignments ?? new Map(),
    recipes: data?.recipes ?? [],
    unassignedRecipes: data?.unassignedRecipes ?? [],
    enabledMealTypes: data?.enabledMealTypes ?? [],

    // Loading & Error States
    isLoading,
    isError,
    error: error as Error | null,

    // Mutations
    assignRecipe: assignMutation.mutate,
    removeAssignment: removeMutation.mutate,
    isAssigning: assignMutation.isPending,
    isRemoving: removeMutation.isPending,

    // Utils
    refetch: () => {
      refetch()
    }
  }
}
